// The client side of /api/persons (BUILD-BRIEF §4, owner: people agent).
//
// The types below MIRROR the server's own response interfaces in
// src/server/routes/persons.ts. They are re-declared rather than imported so the bundle never
// pulls a server module into the browser; when the server shape changes, this file changes with
// it and `bunx tsc --noEmit` is what says so.
//
// Everything the board renders is a column or a sentence the SERVER composed (`note`,
// `next_move.title`). The page arranges; it does not derive. That is the same law Signet's
// clients board runs on, and it is why the board and the brief can never disagree.
import { request, qs } from './client.js';
import type {
  AffiliationView, CommitmentView, Fact, FactMethod, ListResponse, Origin, Person, PersonLanguage,
  PersonProfileResponse, PersonStatus, Register, Tier,
} from '../../shared/types.js';

export type { PersonProfileResponse };

export type { Origin };

// ── the board ───────────────────────────────────────────────────────────────

export interface PersonCard {
  id: number;
  ref: string;
  name: string;
  tier: Tier;
  cadence_days: number;
  status: PersonStatus;
  language: PersonLanguage;
  organisation_id: number | null;
  organisation_ref: string | null;
  organisation_name: string | null;
  role_title: string | null;
  /** The role came from an import or an agent and nobody held it: ◇, not ▪. */
  role_proposed: boolean;
  days_since: number | null;
  last_kind: string | null;
  last_on: string | null;
  ball: 'us' | 'them' | null;
  ball_due: string | null;
  ball_late: boolean;
  ball_line: string | null;
  cooling: boolean;
  is_new: boolean;
  unheld: number;
  opportunity_ref: string | null;
  opportunity_stage: string | null;
  state: 'late' | 'cooling' | 'new' | 'warm' | 'quiet';
  rank: number;
  /** Composed server-side. Rendered unedited. */
  note: string;
}

export interface CandidateCard {
  id: number;
  ref: string;
  name: string;
  role_title: string | null;
  organisation_name: string | null;
  proposal_id: number | null;
  proposal_ref: string | null;
  proposal_principal: string | null;
  note: string;
}

export interface PeopleBoard {
  rail: {
    held: number;
    organisations: number;
    tier_a: number;
    candidates: number;
    ball_us: number;
    ball_late: number;
    ball_them: number;
    cooling: number;
    cooling_tier_a: number;
    new_7d: number;
    new_import: number;
    new_operator: number;
  };
  chips: { needs_move: number; tier_a: number; cooling: number; candidates: number; everyone: number };
  lead: PersonCard | null;
  rows: PersonCard[];
  candidates: CandidateCard[];
  total: number;
}

// ── the standing brief ──────────────────────────────────────────────────────

export interface PersonTimelineItem {
  id: number;
  ref: string;
  kind: string;
  occurred_on: string;
  occurred_at: string | null;
  date_confirmed: boolean;
  title: string;
  summary: string | null;
  organisation_name: string | null;
  is_newest: boolean;
}

export interface NextMove {
  title: string;
  detail: string;
  owner: 'us' | 'them';
  ref: string;
  due_on: string | null;
  late: boolean;
  source_ref: string | null;
}

export interface PersonBrief {
  person: Person;
  /**
   * WHERE THIS PERSON CAME FROM (BUILD-BRIEF-R3b §2b) — derived server-side, never here: the
   * earliest interaction they took part in, or the import that wrote the row when there is
   * none. OPTIONAL, because a brief that carries no origin shows NO Origin line: nobody derived
   * it, and a page that guessed one would be making a claim of its own.
   */
  origin?: Origin | null;
  affiliations: AffiliationView[];
  facts: Fact[];
  commitments: CommitmentView[];
  organisation: { id: number; ref: string; name: string; role_title: string | null; status: string } | null;
  interactions: PersonTimelineItem[];
  interaction_count: number;
  last_interaction: { kind: string; occurred_on: string; title: string; ref: string } | null;
  previous_interaction: { kind: string; occurred_on: string } | null;
  days_since: number | null;
  cooling: boolean;
  ball: { owner: 'us' | 'them' | null; due_on: string | null; late: boolean; us: number; them: number };
  sources: { total: number; facts_with_evidence: number; interactions: number };
  unheld: { facts: number; affiliations: number; total: number };
  confirmed: { facts: number; affiliations: number; total: number };
  next_move: NextMove | null;
}

export interface PersonInput {
  name: string;
  first_name?: string | null;
  last_name?: string | null;
  language?: PersonLanguage;
  register?: Register | null;
  tier?: Tier;
  cadence_days?: number;
  emails?: string[];
  phones?: string[];
  linkedin_url?: string | null;
  location?: string | null;
  status?: PersonStatus;
  do_not_contact?: boolean;
  notes?: string | null;
}

// ── calls ───────────────────────────────────────────────────────────────────

// ── the person raster (BUILD-BRIEF-R7 §4, §6.2) ─────────────────────────────

/**
 * The People page's cell filters (§6.2). Every one is a raster TOKEN, not free text: the server
 * validates each against the cell's own vocabulary and answers 400 with the allowed list, so a
 * page that sends a typo is told rather than shown an empty register.
 *
 * `seniority_min` is a MINIMUM RANK, not a token to match: `cadre_superieur` means «cadre
 * supérieur and above» (§1.2), which is what makes the band's synthetic ranks worth having.
 * Every filter also accepts a comma-separated list of its own tokens.
 */
export interface PersonListFilters {
  /** `qs()` takes a bag of scalars; the named keys below are what the server actually answers. */
  [param: string]: string | number | undefined;
  q?: string;
  tier?: string;
  status?: string;
  limit?: number;
  offset?: number;
  tree_of?: number;
  function_family?: string;
  seniority_min?: string;
  buying_influence?: string;
  work_canton?: string;
  birth_decade?: string;
  years_in_role?: string;
  register_function?: string;
  email_status?: string;
  phone_kind?: string;
  recency_band?: string;
}

/**
 * «n held · m estimated · k from the title» for the filtered set (§6.2). A SEGMENT BUILT ON
 * ESTIMATES IS NEVER SHOWN AS A BARE NUMBER — the page prints all three or none of them, and an
 * export carries the same figures. `cells` names the cells the three describe: the active cell
 * filters, or the three title cells when none is active.
 */
export interface PersonListCounts {
  held: number;
  estimated: number;
  from_title: number;
  cells: string[];
}

/**
 * ONE CELL ON ONE ROW, for the ACTIVE filters only (§6.2, "column chips show the value with its
 * mark"). The page draws the value it filtered on beside the person and NEVER derives it: which
 * fact answers a cell, and whether anybody held it, is the server's rule.
 *
 *   method  'read' · 'estimated' · 'declared' from the fact, or 'computed' when last night's
 *           table answered. The five methods must never look the same (P12).
 *   held    the fact carries `confirmed_at`. A computed value is never held — arithmetic makes
 *           no claim, so its chip carries NO mark at all.
 */
export interface PersonListCell {
  value: string;
  method: FactMethod;
  held: boolean;
}

/** A person as the list answers them, plus the active cells. `cells` is `{}` when nothing filtered. */
export interface PersonListItem extends Person {
  cells: Record<string, PersonListCell>;
}

export interface PersonListResponse extends ListResponse<PersonListItem> {
  counts: PersonListCounts;
}

/**
 * `tree_of` (BUILD-BRIEF-R3b §2b): every person affiliated with an organisation OR with any
 * unit under it, to depth 3. It is a SERVER filter — the tree is a recursive query, and no page
 * is allowed to guess at it from a parent id. Round 7 adds §6.2's ten cell filters, answered
 * server-side from the current `person.*` facts joined with `persons_computed`, where a held,
 * estimated or declared fact always wins over last night's table.
 */
export const listPersons = (params: PersonListFilters = {}) =>
  request<PersonListResponse>(`/persons${qs(params)}`);

/**
 * GET /api/persons/:id/profile — the five blocks, every cell in its state (held · pending ·
 * open · not applicable · not found · computed), the coverage per block and the whole.
 *
 * The shape is `ProfileZone`'s, on purpose: the person zone is a sibling of the organisation's,
 * not a fork. A computed cell carries `basis` and NO claim mark (P12); an `open` cell carries a
 * null value and reads "not collected", never a zero.
 */
export const getPersonProfile = (id: number) =>
  request<PersonProfileResponse>(`/persons/${id}/profile`);

export const getBoard = () => request<PeopleBoard>('/persons/board');

export const getPerson = (id: number) => request<PersonBrief>(`/persons/${id}`);

export const createPerson = (body: PersonInput) =>
  request<Person>('/persons', { method: 'POST', body: JSON.stringify(body) });

export const updatePerson = (id: number, body: Partial<PersonInput>) =>
  request<Person>(`/persons/${id}`, { method: 'PATCH', body: JSON.stringify(body) });

export const archivePerson = (id: number) =>
  request<Person>(`/persons/${id}/archive`, { method: 'POST' });

// ── «Ask Surfe» (BUILD-BRIEF-R7 §5.2) ───────────────────────────────────────

/** Why the paid rung was passed — the server's own vocabulary, translated by the card. */
export type EnrichSkip =
  | 'surfe_off' | 'surfe_exhausted' | 'surfe_limited' | 'surfe_unreachable' | 'surfe_error'
  | 'no_current_affiliation' | 'email_already_held' | 'do_not_contact' | 'not_identifiable'
  | 'surfe_no_email' | 'surfe_pending' | 'surfe_failed';

/**
 * What POST /api/persons/:id/enrich answers, always 200: a proposal now waiting in Review — a
 * broker's assertion is always the operator's click — or the rung passed with its reason. The
 * card renders the code, never derives one; `reason` is the server's sentence for the log.
 */
export interface EnrichAnswer {
  proposal_ref?: string;
  proposal_status?: string;
  snapshot_id?: number;
  validation_status?: string | null;
  email?: string;
  skipped?: EnrichSkip;
  reason?: string;
  credits?: { totalEmail: number; totalMobile: number; totalSearch: number };
  status?: number;
  /** Every proposal the answer produced — the e-mail, and the free cells beside it. */
  filed?: Array<{ key: string; value: string; ref: string; status: string }>;
}

/** The operator's click: one Surfe credit at most, the answer a proposal or a passed rung. */
export const enrichPerson = (id: number) =>
  request<EnrichAnswer>(`/persons/${id}/enrich`, { method: 'POST' });

/**
 * Promote a candidate onto the record.
 *
 * A candidate named by a run has a PROPOSAL behind it, and the only honest way to promote it is
 * to HOLD that proposal: holding is what writes the row, sets `applied_ref` and leaves the
 * reviewer's name on the decision (P1). A candidate row with no proposal behind it — an
 * operator typed it, or the proposal was already disposed of — has nothing to hold, so its
 * status is flipped directly. The two paths are not interchangeable and the caller does not
 * choose between them: the presence of `proposal_id` does.
 */
export async function promoteCandidate(candidate: { id: number; proposal_id: number | null }): Promise<void> {
  if (candidate.proposal_id) {
    await request<unknown>(`/proposals/${candidate.proposal_id}/hold`, { method: 'POST' });
    return;
  }
  await updatePerson(candidate.id, { status: 'held' });
}
